import { useMemo } from 'react';
import { FilterMenu, type FilterOption } from '@/components/shared/filter-menu';
import type { SopCategoryTreeItem } from '@/services/sop.service';

export interface SourcesCategorySelection {
  sopCatId?: string;
  category?: string;
  isUncategorised?: boolean;
}

interface SourcesCategoryFilterProps {
  categories: SopCategoryTreeItem[];
  selected: SourcesCategorySelection;
  onSelect: (selection: SourcesCategorySelection) => void;
  /** The counts are protocol drafts per subcategory; pages listing something else hide them. */
  showCounts?: boolean;
}

const UNCATEGORISED = '__uncategorised';

/** Filters by subcategory (or the uncategorised ones); a category only groups its subcategories. */
export function SourcesCategoryFilter({ categories, selected, onSelect, showCounts = true }: SourcesCategoryFilterProps) {
  const options = useMemo<FilterOption[]>(
    () => [
      ...categories.map((cat) => ({
        label: cat.category,
        children: cat.subcategories.map((sub) => ({
          label: sub.name,
          // A subcategory with no SOP category yet has nothing to filter by.
          value: sub.sopCatId ?? undefined,
          count: showCounts ? sub.count : undefined,
          weight: sub.count
        }))
      })),
      { label: 'Un-Categorised', value: UNCATEGORISED }
    ],
    [categories, showCounts]
  );

  const total = showCounts
    ? categories.reduce((sum, c) => sum + c.subcategories.reduce((s, sub) => s + sub.count, 0), 0)
    : undefined;

  const onChange = (value: string | undefined) => {
    if (!value) return onSelect({});
    if (value === UNCATEGORISED) return onSelect({ isUncategorised: true });
    const category = categories.find((c) => c.subcategories.some((s) => s.sopCatId === value))?.category;
    onSelect({ sopCatId: value, category });
  };

  return (
    <FilterMenu
      label='Categories'
      options={options}
      value={selected.isUncategorised ? UNCATEGORISED : selected.sopCatId}
      onChange={onChange}
      total={total}
    />
  );
}
